/**
 * Enhanced authentication check hook
 * Determines user's auth state and onboarding needs
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useOrganizationQuery } from '@/hooks/queries/use-organization-query';
import { useStore } from '@/store';

export type AuthCheckState =
  | 'loading'
  | 'unauthenticated'
  | 'needs-onboarding'
  | 'authenticated'
  | 'error';

export interface AuthCheckResult {
  state: AuthCheckState;
  user: any | null;
  organization: any | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to check authentication and determine onboarding status
 */
export function useAuthCheck(): AuthCheckResult {
  const { user, isAuthenticated, isLoading: authLoading } = useStore();
  const [state, setState] = useState<AuthCheckState>('loading');
  const [error, setError] = useState<string | null>(null);

  // Query organization only if authenticated
  const {
    data: organization,
    isLoading: orgLoading,
    error: orgError,
    isError: hasOrgError,
  } = useOrganizationQuery();

  useEffect(() => {
    // Still loading auth or org data
    if (authLoading || (isAuthenticated && orgLoading)) {
      setState('loading');
      return;
    }

    // Not authenticated
    if (!isAuthenticated || !user) {
      setState('unauthenticated');
      return;
    }

    // Check if user needs onboarding
    // If org query returns 404 or no organization exists, user needs onboarding
    if (hasOrgError && orgError?.status === 404) {
      setState('needs-onboarding');
      return;
    }

    // If there's another type of error fetching org
    if (hasOrgError && orgError?.status !== 404) {
      setState('error');
      setError('Failed to fetch organization data');
      return;
    }

    // User has an organization - they're fully authenticated
    if (organization) {
      setState('authenticated');
      return;
    }

    // Default to needs onboarding if no org found
    setState('needs-onboarding');
  }, [authLoading, isAuthenticated, user, orgLoading, organization, hasOrgError, orgError]);

  return {
    state,
    user,
    organization,
    isLoading: state === 'loading',
    error,
  };
}

/**
 * Hook to require authentication and handle onboarding redirect
 */
export function useRequireAuthWithOnboarding(redirectTo = '/login') {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, isLoading } = useAuthCheck();

  useEffect(() => {
    if (isLoading) return;

    // Public routes that don't require auth
    const publicRoutes = ['/login', '/auth/login', '/auth/callback', '/logout-success'];
    const isPublicRoute = publicRoutes.includes(location.pathname);

    // Onboarding route
    const isOnboardingRoute = location.pathname.startsWith('/onboarding');

    switch (state) {
      case 'unauthenticated':
        if (!isPublicRoute) {
          navigate(redirectTo, {
            replace: true,
            state: { from: location.pathname },
          });
        }
        break;

      case 'needs-onboarding':
        if (!isOnboardingRoute && !isPublicRoute) {
          navigate('/onboarding', { replace: true });
        }
        break;

      case 'authenticated':
        // If user is on onboarding but already has an org, redirect to dashboard
        if (isOnboardingRoute) {
          navigate('/dashboard', { replace: true });
        }
        // If user is on login page but already authenticated, redirect to dashboard
        else if (isPublicRoute && location.pathname !== '/logout-success') {
          navigate('/dashboard', { replace: true });
        }
        break;

      case 'error':
        // Could redirect to an error page or show error UI
        console.error('Auth check error');
        break;
    }
  }, [state, isLoading, navigate, redirectTo, location]);

  return { state, isLoading };
}

/**
 * Hook to ensure user has completed onboarding
 */
export function useRequireOnboarding() {
  const navigate = useNavigate();
  const { state, organization, isLoading } = useAuthCheck();

  useEffect(() => {
    if (isLoading) return;

    if (state === 'authenticated' && organization) {
      // User has completed onboarding, redirect to dashboard
      navigate('/dashboard', { replace: true });
    } else if (state === 'unauthenticated') {
      // Not authenticated, redirect to login
      navigate('/login', { replace: true });
    }
    // If state === 'needs-onboarding', stay on onboarding page
  }, [state, organization, isLoading, navigate]);

  return {
    canProceed: state === 'needs-onboarding',
    isLoading,
  };
}
